import React, { useCallback, useMemo, useState } from 'react';
import type { CellStyle, ColDef, ValueFormatterParams } from 'ag-grid-community';
import { LopCrudPage } from '../../components/LopCrudPage';
import {
  LopHistoryDateFilter,
  isoToday,
  isoTwoWeeksAgo,
} from '../../components/LopHistoryDateFilter';
import { getBankImportLog } from '../../api/bankImportLog';
import type { BankImportLogRow } from '../../api/bankImportLog';
import { useBanks } from '../../hooks/useBanks';
import { t } from '../../i18n/t';
import { formatDateTimeIL } from '../../utils/dateTime';

// "היסטוריית קליטות" — read-only viewer of dbo.BankImportLog: one row per ingestion RUN,
// whatever its outcome. Same shell as AuditLogPage (LopCrudPage + history date filter +
// no-op saveBatch), and it rides on the `bankFiles` resource rather than minting its own —
// seeing the history and seeing the exceptions are the same privilege.
//
// The window is on startedAt (WHEN WE RAN IT), not the files' value date: a run today can
// carry a post-holiday file dated days back, and the question this screen answers is "did
// last night's Poalim import go through", which is about runs.

/** ms → "42 שנ׳" / "1:23 דק׳" — a raw millisecond count is unreadable at a glance. */
function fmtDuration(ms: number | null | undefined): string {
  if (ms == null) return '';
  const totalSec = Math.round(ms / 1000);
  if (totalSec < 60) return t('bankImportLog.seconds', { n: String(totalSec) });
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return t('bankImportLog.minutes', { n: `${m}:${String(s).padStart(2, '0')}` });
}

export function BankImportLogPage() {
  const { data: banks } = useBanks();

  const [draftFrom, setDraftFrom] = useState<string>(isoTwoWeeksAgo);
  const [draftTo, setDraftTo] = useState<string>(isoToday);
  const [activeFrom, setActiveFrom] = useState<string>(draftFrom);
  const [activeTo, setActiveTo] = useState<string>(draftTo);
  // No data is loaded until the first click on "Fetch" (see LopCrudPage.awaitingFetch).
  const [fetched, setFetched] = useState<boolean>(false);

  const bankNameById = useMemo(
    () => new Map<number, string>((banks ?? []).map((b) => [b.bankId, b.bankName])),
    [banks],
  );

  const columnDefs = useMemo<ColDef<BankImportLogRow>[]>(
    () => [
      {
        field: 'startedAt',
        headerName: t('bankImportLog.columns.startedAt'),
        width: 160,
        sort: 'desc',
        valueFormatter: (p: ValueFormatterParams<BankImportLogRow>) => formatDateTimeIL(p.value, { seconds: true }),
      },
      {
        field: 'status',
        headerName: t('bankImportLog.columns.status'),
        width: 100,
        valueFormatter: (p: ValueFormatterParams<BankImportLogRow>) =>
          p.value ? t(`bankImportLog.status.${p.value}` as Parameters<typeof t>[0]) : '',
        // A failed run must be findable by eye in a long list — this screen exists to be
        // scanned, and a red cell is what an operator actually looks for.
        cellStyle: (p): CellStyle | null =>
          p.value === 'failed'
            ? { color: 'var(--color-error-text)', fontWeight: 600 }
            : p.value === 'running'
              ? { color: 'var(--color-warning-text)', fontWeight: 400 }
              : null,
      },
      {
        field: 'importName',
        headerName: t('bankImportLog.columns.importName'),
        flex: 1,
        minWidth: 200,
      },
      {
        field: 'bankId',
        headerName: t('bankImportLog.columns.bankId'),
        width: 150,
        // Orphan/unknown bank ids fall back to the raw number rather than showing blank.
        valueGetter: (p) =>
          p.data ? (bankNameById.get(p.data.bankId) ?? String(p.data.bankId)) : '',
      },
      {
        field: 'valueDate',
        headerName: t('bankImportLog.columns.valueDate'),
        width: 120,
      },
      {
        field: 'durationMs',
        headerName: t('bankImportLog.columns.duration'),
        width: 110,
        valueFormatter: (p: ValueFormatterParams<BankImportLogRow>) =>
          fmtDuration(p.value as number | null),
      },
      {
        field: 'filesReceived',
        headerName: t('bankImportLog.columns.filesReceived'),
        width: 110,
        cellDataType: 'number',
      },
      {
        field: 'filesLoaded',
        headerName: t('bankImportLog.columns.filesLoaded'),
        width: 110,
        cellDataType: 'number',
      },
      {
        field: 'rowsLoaded',
        headerName: t('bankImportLog.columns.rowsLoaded'),
        width: 120,
        cellDataType: 'number',
      },
      {
        field: 'fileNames',
        headerName: t('bankImportLog.columns.fileNames'),
        flex: 1,
        minWidth: 220,
        tooltipField: 'fileNames',
      },
      {
        field: 'filePattern',
        headerName: t('bankImportLog.columns.filePattern'),
        width: 150,
      },
      {
        field: 'messages',
        headerName: t('bankImportLog.columns.messages'),
        flex: 2,
        minWidth: 300,
        tooltipField: 'messages',
      },
      {
        field: 'createdBy',
        headerName: t('bankImportLog.columns.createdBy'),
        width: 180,
      },
    ],
    [bankNameById],
  );

  const getRows = useCallback(
    () => getBankImportLog(activeFrom, activeTo),
    [activeFrom, activeTo],
  );
  const saveBatch = useCallback(async () => [], []);
  const newRowDefaults = useCallback((): Partial<BankImportLogRow> => ({}), []);

  return (
    <div className="lop-history-page">
      <LopHistoryDateFilter
        fromDate={draftFrom}
        toDate={draftTo}
        onFromChange={setDraftFrom}
        onToChange={setDraftTo}
        onApply={() => {
          setActiveFrom(draftFrom);
          setActiveTo(draftTo);
          setFetched(true);
        }}
      />
      <LopCrudPage<BankImportLogRow>
        awaitingFetch={!fetched}
        resourceCode="bankFiles"
        titleKey="bankImportLog.pageTitle"
        idField="logId"
        getRows={getRows}
        saveBatch={saveBatch}
        columnDefs={columnDefs}
        newRowDefaults={newRowDefaults}
        showAdd={false}
        showDelete={false}
      />
    </div>
  );
}
